import { useCallback, useEffect, useRef, useState } from "react";

import axios from "axios";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { useSse, type SseMessage } from "@platform/hooks/useSse";

import type { WsDeploymentEvent } from "./useDeploymentStream";
import { useStackRealtime } from "./useStackRealtime";

// ── Types ─────────────────────────────────────────────────────────────────────

interface StatusChangedEvent {
  systemId: number;
  systemAlias: string;
  previousStatus: string;
  newStatus: string;
  organizationId: number;
  changedBy: number;
}

export interface UseStackStatusResult {
  status: string | null;
  deploymentUrl: string | null;
  connected: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Subscribes to real-time system status updates via SSE.
 * Falls back to REST polling every 10s when disconnected.
 *
 * When inside a StackRealtimeProvider, returns the shared context data
 * instead of opening its own SSE connection.
 *
 * Events listened for:
 *  - "status_changed" → STATUS_CHANGED events
 *  - "deployment"     → DEPLOY_COMPLETED / DEPLOY_FAILED carry implicit status
 */
export function useStackStatus(
  systemAlias: string | undefined,
): UseStackStatusResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Local state (always declared to satisfy rules-of-hooks) ──────────────
  const [status, setStatus] = useState<string | null>(null);
  const [deploymentUrl, setDeploymentUrl] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  /** Poll REST metrics endpoint as fallback when SSE is disconnected */
  const pollMetrics = useCallback(async () => {
    if (!systemAlias) return;
    try {
      const url = `${apiBaseUrls.BACKEND}/api/v1/applications/${systemAlias}/metrics`;
      const res = await axios.get<{
        status: string;
        deploymentUrl?: string | null;
      }>(url, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setStatus(res.data.status ?? null);
      if (res.data.deploymentUrl) {
        setDeploymentUrl(res.data.deploymentUrl);
      }
    } catch {
      // Silently ignore — next poll will retry
    }
  }, [systemAlias]);

  // Initial REST fetch to seed status
  useEffect(() => {
    if (!systemAlias) return;
    void pollMetrics();
  }, [systemAlias, pollMetrics]);

  const handleMessage = useCallback(
    (msg: SseMessage) => {
      if (msg.type === "connected") {
        setConnected(true);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        return;
      }

      if (msg.type === "status_changed") {
        const data = msg.data as StatusChangedEvent;
        if (data.systemAlias === systemAlias) {
          setStatus(data.newStatus);
        }
        return;
      }

      if (msg.type === "deployment") {
        const data = msg.data as WsDeploymentEvent;
        if (data.systemAlias !== systemAlias) return;

        const eventType = data.eventType;
        if (
          eventType === "deploy_completed" ||
          eventType === "DEPLOY_COMPLETED"
        ) {
          setStatus("running");
          if (data.metadata?.ingressUrl) {
            setDeploymentUrl(data.metadata.ingressUrl as string);
          }
        } else if (
          eventType === "deploy_failed" ||
          eventType === "DEPLOY_FAILED"
        ) {
          setStatus("failed");
        }
        return;
      }
    },
    [systemAlias],
  );

  const ssePath = systemAlias ? `sse/stack/${systemAlias}` : null;
  const sse = useSse(ssePath, handleMessage);

  // Sync SSE connection state
  useEffect(() => {
    setConnected(sse.connected);
    if (!sse.connected && !pollRef.current && systemAlias) {
      // Start polling fallback when SSE disconnects
      void pollMetrics();
      pollRef.current = setInterval(() => void pollMetrics(), 10_000);
    } else if (sse.connected && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [sse.connected, systemAlias, pollMetrics]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      status: ctx.status,
      deploymentUrl: ctx.deploymentUrl,
      connected: ctx.deploymentConnected,
    };
  }

  return { status, deploymentUrl, connected };
}
